import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { testimonials, faqItems } from '@/mocks/tvSeries';

export default function AboutPage() {
  const { t } = useTranslation();

  const breadcrumbItems = [{ label: t('about.title') }];

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 四虎影视大全',
    description: '了解四虎影视大全的品牌故事、用户口碑与联系方式。国内领先的电视剧全集免费在线观看平台。',
    url: 'https://www.sihutv.com/about',
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '四虎影视大全',
      url: 'https://www.sihutv.com/',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.sihutv.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '关于我们',
          item: 'https://www.sihutv.com/about',
        },
      ],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <JsonLd data={aboutPageSchema} />
      <JsonLd data={faqSchema} />
      <Navbar />
      <div className="pt-16">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <main>
          {/* Page Header */}
          <section className="relative px-4 md:px-6 py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20with%20soft%20amber%20and%20coral%20tones%2C%20gentle%20flowing%20shapes%2C%20minimalist%20modern%20design%2C%20elegant%20background%2C%20soft%20light%20effect%2C%20professional%20corporate%20style&width=1800&height=500&seq=about-header-bg&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-background-900/60" />
            <div className="relative z-10 max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-4">
                {t('about.title')}
              </h1>
              <p className="text-sm md:text-base text-background-50/70">
                {t('siteDescription')}
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                {t('about.brandTitle')}
              </h2>
              <div className="prose max-w-none">
                <p className="text-foreground-600 leading-relaxed mb-6">
                  {t('about.brandDesc1')}
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  {t('about.brandDesc2')}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                <StatCard
                  icon="ri-user-heart-line"
                  value="1,200万+"
                  label={t('about.statsUsers')}
                />
                <StatCard
                  icon="ri-movie-line"
                  value="50,000+"
                  label={t('about.statsSeries')}
                />
                <StatCard
                  icon="ri-stack-line"
                  value="8"
                  label={t('about.statsCategories')}
                />
                <StatCard
                  icon="ri-thumb-up-line"
                  value="98.5%"
                  label={t('about.statsSatisfaction')}
                />
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
                  {t('about.reputationTitle')}
                </h2>
                <p className="text-sm text-foreground-500">
                  {t('about.reputationDesc')}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {testimonials.map((tm) => (
                  <div
                    key={tm.id}
                    className="p-6 rounded-lg bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 flex-shrink-0 rounded-full overflow-hidden">
                        <img
                          src={tm.avatar}
                          alt={tm.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <strong className="text-sm font-heading text-foreground-900">
                            {tm.name}
                          </strong>
                          <span className="text-xs text-foreground-400">{tm.date}</span>
                        </div>
                        <div className="flex items-center gap-0.5 mb-2">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                              <i
                                className={`text-xs ${
                                  i < tm.rating
                                    ? 'ri-star-fill text-secondary-400'
                                    : 'ri-star-line text-foreground-300'
                                }`}
                              ></i>
                            </div>
                          ))}
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {tm.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="px-4 md:px-6 py-14 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-10 text-center">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqItems.map((item, index) => (
                  <details
                    key={index}
                    className="group rounded-lg bg-background-100 border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-200/50 transition-colors">
                      <span className="text-sm font-medium text-foreground-800 pr-4">
                        {item.question}
                      </span>
                      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center text-foreground-400 group-open:rotate-180 transition-transform">
                        <i className="ri-arrow-down-s-line"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="px-4 md:px-6 py-14 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
                  {t('about.contactTitle')}
                </h2>
                <p className="text-sm text-foreground-500">
                  {t('about.contactDesc')}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <ContactCard
                  icon="ri-mail-line"
                  label={t('about.email')}
                  value={t('about.emailValue')}
                />
                <ContactCard
                  icon="ri-wechat-line"
                  label={t('about.wechat')}
                  value={t('about.wechatValue')}
                />
                <ContactCard
                  icon="ri-weibo-line"
                  label={t('about.weibo')}
                  value={t('about.weiboValue')}
                />
                <ContactCard
                  icon="ri-phone-line"
                  label={t('about.phone')}
                  value={t('about.phoneValue')}
                />
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </>
  );
}

function StatCard({
  icon,
  value,
  label,
}: {
  icon: string;
  value: string;
  label: string;
}) {
  return (
    <div className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
      <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50">
        <i className={`${icon} text-xl text-primary-500`}></i>
      </div>
      <div className="text-2xl font-heading font-bold text-foreground-900 mb-1">{value}</div>
      <div className="text-xs text-foreground-500">{label}</div>
    </div>
  );
}

function ContactCard({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-50">
        <i className={`${icon} text-lg text-accent-500`}></i>
      </div>
      <div>
        <p className="text-xs text-foreground-500 mb-0.5">{label}</p>
        <p className="text-sm font-medium text-foreground-800">{value}</p>
      </div>
    </div>
  );
}